import { Injectable } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { DriversIntegrationTypes, FilesUploadBody, FilesUploadResponse } from '../projects/document-scan/drivers-integrations/drivers-integrations.types';
import { Endpoints } from '../endpoints';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

type FetchRequestInit = RequestInit & {
  duplex?: 'half';
};

@Injectable({
  providedIn: 'root',
})
export class UploadFileService {
  constructor(
    private readonly storage: AngularFireStorage,
    private readonly httpClient: HttpClient,
    private readonly afAuth: AngularFireAuth,
  ) { }

  getFileUploadStatus(scanProjectId: string) {
    return this.httpClient.get<FilesUploadResponse>(Endpoints.getProviderFileUploadStatus(scanProjectId));
  }

  uploadExternalProviderFiles(vendor: DriversIntegrationTypes, files: FilesUploadBody, scanProjectId: string) {
    return this.httpClient.post<FilesUploadResponse>(Endpoints.uploadExternalFiles(vendor, scanProjectId), { ...files });
  }

  uploadLocalPickedFiles(files: File[], scanProjectId: string) {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files[]', file, file.name);
    }

    return this.httpClient.post<FilesUploadResponse>(
      Endpoints.uploadLocalPickedFiles(scanProjectId),
      formData
    );
  }

  async uploadAnnotatedPdf(
    projectId: string,
    filePath: string,
    pdfBlob: Blob,
    isImage: boolean
  ): Promise<void> {
    const token = await firstValueFrom(this.afAuth.idToken.pipe(take(1)));
    if (!token) {
      throw new Error('No authentication token available');
    }

    const uploadEndpoint = `${environment.cfBackend}/projects/${projectId}/files/upload-annotated`;
    const requestInit: FetchRequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/pdf',
        'X-File-Path': `${filePath}${isImage ? '.pdf' : ''}`,
        'Authorization': `Bearer ${token}`,
      },
      body: pdfBlob.stream(),
      duplex: 'half'
    };
    const response = await fetch(uploadEndpoint, requestInit);
    if (!response.ok) {
      throw new Error('Failed to upload annotated PDF');
    }
  }

  /***
   * @deprecated Remove me when the document scan and media file scan are merged
   */
  uploadAudioOrVideoFile(file: File, filePath: string): Observable<any> {
    return new Observable<{ percentage: number, filePath: string | null }>((observer) => {
      const task = this.storage.upload(filePath, file);
      const percentage = task.percentageChanges();
      // observe percentage changes
      percentage.subscribe((percentage) => {
        observer.next({ percentage: Number(percentage), filePath: null }); // Emitting upload percentage
      });

      task
        .snapshotChanges()
        .toPromise()
        .then(() => {
          this.storage
            .ref(filePath)
            .getDownloadURL()
            .toPromise()
            .then(() => {
              observer.next({ percentage: Number(percentage), filePath });
              observer.complete();
            })
            .catch((error) => {
              observer.error(error);
            });
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }
}
